import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getConfig, updateConfig, SystemConfig, healthCheck, HealthStatus, getDatabaseMetrics, DatabaseMetrics, getAllApiKeys, ApiKey, createApiKey, deleteApiKey, getSystemLogs, clearSystemLogs, SystemLog, getBackups, createBackup, deleteBackup, restoreBackup, getBackupDownloadUrl, BackupInfo } from '../services/api';
import ApiGuide from '../components/ApiGuide';
import CleanupManager from './admin/CleanupManager';
import ModelCatalogManager from './admin/ModelCatalogManager';
// Lazy: the WebUSB/ADB code is only loaded when an admin opens Deployment (route /Settings/usb).
const UsbInstaller = React.lazy(() => import('./admin/UsbInstaller'));
import { useIdentity } from '../services/identity';
import AssignManager from './admin/AssignManager';
import InventoryManager from './admin/InventoryManager';
import PlatformAdministrators from './admin/PlatformAdministrators';
import TenantsManager from './admin/TenantsManager';
import People from './admin/People';
import { INTERNAL_COMPONENTS, PLATFORM_BUILD, SERVICE_VERSIONS } from '../buildInfo';
import ConfigurationArea from './admin/configuration/ConfigurationArea';
import OEMPartnersManager from './admin/OEMPartnersManager';
import { isSubsection, replacementSubsection } from './admin/configuration/configurationSections';
import { SETTINGS_SECTIONS, retiredSettingsSection, settingsTabFor, visibleSettingsTabs, type SettingsTab } from './settingsTabs';

export default function Settings() {
  const { section, subsection } = useParams<{ section?: string; subsection?: string }>();
  const navigate = useNavigate();
  
  const defaultTab: SettingsTab = 'configuration';
  const { me } = useIdentity();
  // Only Customers is a tenant-level screen; every other Settings section is platform
  // administration (the server refuses it to others regardless - this only hides it).
  const isPlatform = !me || me.platform_admin;
  const managesPeople = !!me?.manages_users;
  const requestedTab: SettingsTab = settingsTabFor(section) || (isPlatform ? defaultTab : 'customers');
  const activeTab: SettingsTab = isPlatform || requestedTab === 'customers' || (managesPeople && requestedTab === 'people') ? requestedTab : 'customers';
  // A retired Configuration sub-section (e.g. /Settings/config/ovhcloud) shows its replacement and
  // the address is rewritten, so there is never a second, stale provider page.
  const retiredTo = activeTab === 'configuration' ? replacementSubsection(subsection) : undefined;
  useEffect(() => {
    if (retiredTo) navigate(`/Settings/config/${retiredTo}`, { replace: true });
  }, [retiredTo, navigate]);
  // A retired section (the old /Settings/Network page) opens its replacement, so bookmarks keep working.
  const retiredSection = retiredSettingsSection(section);
  useEffect(() => {
    if (retiredSection) navigate(`/Settings/${retiredSection}`, { replace: true });
  }, [retiredSection, navigate]);
  const [config, setConfig] = useState<SystemConfig | null>(null);
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [databaseMetrics, setDatabaseMetrics] = useState<DatabaseMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [systemLogs, setSystemLogs] = useState<SystemLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logLevel, setLogLevel] = useState<string>('all');
  const [newApiKeyName, setNewApiKeyName] = useState('');

  // Refs so the auto-refresh interval (mounted once) always sees current values
  // without needing to depend on them (which would reset the timer every change).
  const editingRef = useRef(editing);
  useEffect(() => {
    editingRef.current = editing;
  }, [editing]);
  const logLevelRef = useRef(logLevel);
  useEffect(() => {
    logLevelRef.current = logLevel;
  }, [logLevel]);

  useEffect(() => {
    loadSettings();
    loadHealth();
    loadDatabaseMetrics();
    loadApiKeys();
    loadLogs();
    // Set a timeout to ensure we don't stay in loading state forever
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 5000);
    const interval = setInterval(() => {
      // Skip the config reload while the admin is mid-edit so it can't overwrite unsaved changes.
      if (!editingRef.current) loadSettings(true);
      loadHealth();
      loadDatabaseMetrics();
      loadApiKeys(true);
      loadLogs(logLevelRef.current, true);
    }, 5000);
    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, []);

  const handleTabChange = (tab: SettingsTab) => {
    const urlSection = SETTINGS_SECTIONS.find((x) => x.id === tab)?.path ?? tab;
    navigate(`/Settings/${urlSection}`);
  };

  const loadSettings = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const data = await getConfig();
      setConfig(data);
      setError(null);
    } catch (err) {
      // If settings fail to load, just continue - other admin features still work
      console.warn('Settings API not available, continuing with admin features');
      setConfig(null);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadHealth = async () => {
    try {
      const data = await healthCheck();
      setHealth(data);
    } catch (err) {
      console.warn('Health check not available:', err);
      // Health is optional, don't show error
    }
  };

  const loadDatabaseMetrics = async () => {
    try {
      setDatabaseMetrics(await getDatabaseMetrics());
    } catch (err) {
      console.warn('Database metrics not available:', err);
      setDatabaseMetrics(null);
    }
  };

  const loadApiKeys = async (silent = false) => {
    try {
      if (!silent) setApiKeysLoading(true);
      const keys = await getAllApiKeys();
      setApiKeys(keys);
    } catch (err) {
      console.warn('Failed to load API keys, using empty list:', err);
      setApiKeys([]);
    } finally {
      if (!silent) setApiKeysLoading(false);
    }
  };

  const loadLogs = async (level: string = logLevel, silent = false) => {
    try {
      if (!silent) setLogsLoading(true);
      const logs = await getSystemLogs(level, 200);
      setSystemLogs(logs);
    } catch (err) {
      console.warn('Failed to load system logs:', err);
      setSystemLogs([]);
    } finally {
      if (!silent) setLogsLoading(false);
    }
  };

  const handleClearLogs = async () => {
    try {
      await clearSystemLogs();
      await loadLogs();
    } catch (err) {
      console.warn('Failed to clear logs:', err);
    }
  };

  const handleLogLevelChange = (level: string) => {
    setLogLevel(level);
    loadLogs(level);
  };

  const handleSave = async () => {
    if (!config) return;
    try {
      setSaveSuccess(false);
      await updateConfig(config);
      setSaveSuccess(true);
      setEditing(false);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(`Failed to save settings: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleChange = (path: string, value: any) => {
    if (!config) return;
    
    const keys = path.split('.');
    const newConfig = JSON.parse(JSON.stringify(config));
    
    let current = newConfig;
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
    
    setConfig(newConfig);
  };

  const handleGenerateApiKey = async () => {
    if (newApiKeyName) {
      try {
        const newKey = await createApiKey(newApiKeyName);
        setApiKeys([...apiKeys, newKey]);
        setNewApiKeyName('');
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } catch (err) {
        setError(`Failed to generate API key: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    }
  };

  const handleRevokeApiKey = async (id: string) => {
    try {
      await deleteApiKey(id);
      setApiKeys(apiKeys.filter(k => k.id !== id));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(`Failed to revoke API key: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  const configAvailable = config !== null;

  const sectionConfig = SETTINGS_SECTIONS.map(({ id, icon, label }) => [id, { icon, label }] as [SettingsTab, { icon: string; label: string }]);

  const handleSectionChange = (tab: SettingsTab) => {
    handleTabChange(tab);
  };

  return (
    <div className="h-full min-h-0 bg-gray-900 text-white">
      <div className="flex h-full min-h-0">
        {/* Sidebar Navigation */}
        <aside className="sticky top-0 self-start h-full w-56 bg-gray-900 border-r border-gray-700 overflow-y-auto z-20 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
          <nav className="p-3 space-y-2">
            {visibleSettingsTabs(sectionConfig, isPlatform, managesPeople).map(([id, { icon, label }]) => (
              <button
                key={id}
                onClick={() => handleSectionChange(id)}
                className={`w-full text-left px-4 py-3 rounded-lg font-medium transition ${
                  activeTab === id
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-300 hover:bg-gray-700'
                }`}
              >
                <span className="mr-2">{icon}</span>
                {label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-h-0 overflow-hidden flex flex-col">
          {/* Header */}
          <header className="sticky top-0 z-30 bg-blue-700 shadow-lg shrink-0">
            <div className="px-4 py-4 bg-blue-800 border-b border-blue-700">
              <h1 className="text-2xl font-bold">System Settings & Administration</h1>
              <p className="text-gray-200 text-sm mt-1">Configure services, manage users, and monitor system health</p>
            </div>
          </header>

          {/* Content Area */}
          <div className="flex-1 min-h-0 overflow-y-auto p-8">
            {/* Error/Info Alert */}
            {!configAvailable && activeTab === 'configuration' && subsection === 'runtime' && (
              <div className="mb-8 bg-yellow-900/30 border border-yellow-600 rounded-lg p-4">
                <h3 className="text-yellow-400 font-semibold mb-1">Configuration Not Available</h3>
                <p className="text-sm text-yellow-300">The configuration API is not currently available. Other administration features are fully functional.</p>
              </div>
            )}

            {error && (
              <div className="mb-8 bg-red-900/30 border border-red-500 rounded-lg p-4">
                <h3 className="text-red-400 font-semibold mb-1">Error</h3>
                <p className="text-sm text-red-300">{error}</p>
              </div>
            )}

            {/* Success Alert */}
            {saveSuccess && (
              <div className="mb-8 bg-green-900/30 border border-green-500 rounded-lg p-4">
                <p className="text-sm text-green-300">✓ Operation completed successfully!</p>
              </div>
            )}

            {/* Assign Radios Tab */}
            {activeTab === 'assign' && <AssignManager />}

            {/* Tenancy foundation: customers/departments/roles and the rental inventory */}
            {activeTab === 'customers' && <TenantsManager />}
            {activeTab === 'people' && <People />}
            {activeTab === 'oemPartners' && <OEMPartnersManager />}
            {activeTab === 'inventory' && <InventoryManager />}

            {/* Settings -> Configuration: integrations and infrastructure (documentation/configuration.md);
                the existing runtime-services editor is its "Runtime services" sub-section. */}
            {activeTab === 'configuration' && (
              <ConfigurationArea
                subsection={isSubsection(subsection) ? subsection : retiredTo ?? 'deployment'}
                onNavigate={(sub) => navigate(`/Settings/config/${sub}`)}
                runtime={configAvailable ? (
                  <ConfigurationTab config={config} editing={editing} setEditing={setEditing} 
                                    handleChange={handleChange} handleSave={handleSave} 
                                    loadSettings={loadSettings} health={health} loadHealth={loadHealth} databaseMetrics={databaseMetrics} />
                ) : (
                  <div className="bg-gray-800 rounded-lg p-6">
                    <h2 className="text-2xl font-bold mb-4">Runtime services</h2>
                    <p className="text-gray-400">The runtime configuration API is not currently available. Please check that the backend API is running.</p>
                  </div>
                )}
              />
            )}

            {/* Users & Admins Tab */}
            {activeTab === 'users' && <PlatformAdministrators />}

            {/* API Keys Tab */}
            {activeTab === 'api' && (
              <ApiKeysTab apiKeys={apiKeys} newApiKeyName={newApiKeyName}
                         setNewApiKeyName={setNewApiKeyName} handleGenerateApiKey={handleGenerateApiKey}
                         handleRevokeApiKey={handleRevokeApiKey} />
            )}

            {/* System Tab */}
            {activeTab === 'system' && (
              <SystemTab health={health} loadHealth={loadHealth} />
            )}

            {/* Logs Tab */}
            {activeTab === 'logs' && (
              <LogsTab
                systemLogs={systemLogs}
                loading={logsLoading}
                level={logLevel}
                onRefresh={() => loadLogs()}
                onClear={handleClearLogs}
                onLevelChange={handleLogLevelChange}
              />
            )}

            {/* Backup & Restore Tab */}
            {activeTab === 'backup' && (
              <BackupRestoreTab />
            )}

            {/* Cleanup & Retention Tab */}
            {activeTab === 'cleanup' && (
              <CleanupManager />
            )}

            {/* Model Catalog Tab */}
            {activeTab === 'modelCatalog' && (
              <ModelCatalogManager />
            )}

            {/* Deployment tab (/Settings/usb; formerly "USB Installer"): provisioning and radio apps over WebUSB */}
            {activeTab === 'usbInstaller' && (
              <React.Suspense fallback={<div className="text-gray-400">Loading Deployment…</div>}>
                <UsbInstaller />
              </React.Suspense>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

// Sub-components for each tab
function BuildInventory({ health }: { health: HealthStatus | null }) {
  return (
    <section className="mb-8 grid grid-cols-1 xl:grid-cols-2 gap-6">
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Services</h2>
            <p className="text-sm text-gray-400">External runtime services</p>
          </div>
          <span className="text-xs font-mono text-gray-300 whitespace-nowrap">{PLATFORM_BUILD.version}</span>
        </div>
        <div className="space-y-3">
          {SERVICE_VERSIONS.map((service) => (
            <div key={service.name} className="flex items-center justify-between gap-4 border-b border-gray-700 pb-3 last:border-0 last:pb-0">
              <span className="text-sm font-medium text-white">{service.name}</span>
              <span className="text-sm text-gray-300 text-right">{service.version}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-gray-800 border border-gray-700 rounded-lg p-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Internal Components</h2>
            <p className="text-sm text-gray-400">Changes in build {PLATFORM_BUILD.buildNumber}</p>
          </div>
          <span className="text-xs font-mono text-gray-300 whitespace-nowrap">{PLATFORM_BUILD.version}</span>
        </div>
        <div className="space-y-3">
          {INTERNAL_COMPONENTS.map((component) => (
              <div key={component.name} className="border-b border-gray-700 pb-3 last:border-0 last:pb-0">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm font-medium text-white">{component.name}</span>
                  <span className={`text-xs font-semibold px-2 py-1 rounded whitespace-nowrap ${component.updated ? 'bg-green-900/50 text-green-300' : 'bg-gray-700 text-gray-300'}`}>
                    {component.updated ? `Updated in ${component.lastUpdatedBuild}` : `Build ${component.lastUpdatedBuild}`}
                  </span>
                </div>
                <p className="mt-1 text-sm text-gray-300">{component.version}</p>
                <p className="mt-1 text-xs text-gray-500">{component.detail}</p>
              </div>
            ))}
        </div>
      </div>
    </section>
  );
}

function DatabaseMetricsPanel({ metrics }: { metrics: DatabaseMetrics | null }) {
  const rows = metrics ? [
    ['Database size', metrics.database_size],
    ['Tables', metrics.table_count.toLocaleString()],
    ['Estimated rows', metrics.estimated_row_count.toLocaleString()],
    ['Connections', `${metrics.active_connections} active / ${metrics.connection_count} total`],
    ['PostgreSQL', metrics.postgres_version],
    ['Started', new Date(metrics.server_started_at).toLocaleString()],
  ] : [];

  return (
    <aside className="bg-gray-800 rounded-lg border border-gray-700 p-6">
      <h2 className="text-xl font-bold text-white">Database Insights</h2>
      <p className="mt-1 text-sm text-gray-400">Live PostgreSQL capacity and activity.</p>
      {metrics ? (
        <dl className="mt-5 space-y-3 text-sm">
          {rows.map(([label, value]) => (
            <div key={label} className="border-b border-gray-700 pb-3 last:border-0 last:pb-0">
              <dt className="text-gray-400">{label}</dt>
              <dd className="mt-1 font-medium text-white break-words">{value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="mt-5 text-sm text-gray-500">Database metrics are unavailable.</p>
      )}
    </aside>
  );
}

function ConfigurationTab({ config, editing, setEditing, handleChange, handleSave, loadSettings, health, loadHealth, databaseMetrics }: any) {
  return (
    <div>
      {/* Health Status */}
      {health && (
        <div className="mb-8 bg-green-900/30 border border-green-500 rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-2 text-green-400">✓ System Status</h2>
          <div className="space-y-1 text-sm text-gray-300">
            <p><span className="text-gray-400">Status:</span> {health.status}</p>
            <p><span className="text-gray-400">Platform build:</span> {PLATFORM_BUILD.version}</p>
            <p><span className="text-gray-400">Last Updated:</span> {new Date(health.timestamp).toLocaleString()}</p>
          </div>
        </div>
      )}

      <BuildInventory health={health} />

      {config && (
        <>
          {/* Server Configuration */}
          <div className="bg-gray-800 rounded-lg p-6 mb-6">
            <h2 className="text-2xl font-bold mb-4">Server</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Port</label>
                <input
                  type="number"
                  value={config.port}
                  onChange={(e) => handleChange('port', parseInt(e.target.value))}
                  disabled={!editing}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>
          </div>

          {/* Database Configuration */}
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,2fr)_minmax(300px,1fr)] gap-6 mb-6">
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4">Database</h2>
              <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Host</label>
                  <input
                    type="text"
                    value={config.database.host}
                    onChange={(e) => handleChange('database.host', e.target.value)}
                    disabled={!editing}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Port</label>
                  <input
                    type="number"
                    value={config.database.port}
                    onChange={(e) => handleChange('database.port', parseInt(e.target.value))}
                    disabled={!editing}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">User</label>
                  <input
                    type="text"
                    value={config.database.user}
                    onChange={(e) => handleChange('database.user', e.target.value)}
                    disabled={!editing}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Database Name</label>
                  <input
                    type="text"
                    value={config.database.database}
                    onChange={(e) => handleChange('database.database', e.target.value)}
                    disabled={!editing}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">SSL Mode</label>
                <select
                  value={config.database.sslMode}
                  onChange={(e) => handleChange('database.sslMode', e.target.value)}
                  disabled={!editing}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="disable">Disable</option>
                  <option value="require">Require</option>
                  <option value="prefer">Prefer</option>
                </select>
              </div>
              </div>
            </div>
            <DatabaseMetricsPanel metrics={databaseMetrics} />
          </div>

          {/* Redis Configuration */}
          <div className="bg-gray-800 rounded-lg p-6 mb-6">
            <h2 className="text-2xl font-bold mb-4">Redis</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">URL</label>
                <input
                  type="text"
                  value={config.redis.url}
                  onChange={(e) => handleChange('redis.url', e.target.value)}
                  disabled={!editing}
                  placeholder="redis://localhost:6379"
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed font-mono text-sm"
                />
              </div>
            </div>
          </div>

          {/* LiveKit Configuration */}
          <div className="bg-gray-800 rounded-lg p-6 mb-6">
            <h2 className="text-2xl font-bold mb-4">LiveKit</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">WebSocket URL</label>
                <input
                  type="text"
                  value={config.livekit.url}
                  onChange={(e) => handleChange('livekit.url', e.target.value)}
                  disabled={!editing}
                  placeholder="ws://localhost:7880"
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">API Key</label>
                <input
                  type="password"
                  value={config.livekit.apiKey}
                  onChange={(e) => handleChange('livekit.apiKey', e.target.value)}
                  disabled={!editing}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            {!editing ? (
              <button
                onClick={() => setEditing(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              >
                Edit Settings
              </button>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
                >
                  Save Settings
                </button>
                <button
                  onClick={() => {
                    setEditing(false);
                    loadSettings();
                  }}
                  className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
                >
                  Cancel
                </button>
              </>
            )}
            <button
              onClick={loadHealth}
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded"
            >
              Refresh Status
            </button>
          </div>
        </>
      )}
    </div>
  );
}


function ApiKeysTab({ apiKeys, newApiKeyName, setNewApiKeyName, handleGenerateApiKey, handleRevokeApiKey }: any) {
  const [showGuide, setShowGuide] = useState(false);

  return (
    <div>
      {/* Tab Toggle */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setShowGuide(false)}
          className={`px-4 py-2 rounded font-medium transition ${
            !showGuide
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          🔑 Key Management
        </button>
        <button
          onClick={() => setShowGuide(true)}
          className={`px-4 py-2 rounded font-medium transition ${
            showGuide
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          📖 API Guide
        </button>
      </div>

      {/* Key Management View */}
      {!showGuide && (
        <>
          {/* Generate New API Key */}
          <div className="bg-gray-800 rounded-lg p-6 mb-6">
            <h2 className="text-2xl font-bold mb-4">Generate New API Key</h2>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="API Key Name (e.g., Mobile App, Desktop Client)"
                value={newApiKeyName}
                onChange={(e) => setNewApiKeyName(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-400"
              />
              <button
                onClick={handleGenerateApiKey}
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
              >
                Generate API Key
              </button>
            </div>
          </div>

          {/* API Keys List */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Active API Keys</h2>
            {apiKeys.length === 0 ? (
              <p className="text-gray-400">No API keys yet. Create one using the form above.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-2 px-4">Name</th>
                      <th className="text-left py-2 px-4">Key</th>
                      <th className="text-left py-2 px-4">Created</th>
                      <th className="text-left py-2 px-4">Last Used</th>
                      <th className="text-left py-2 px-4">Status</th>
                      <th className="text-left py-2 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiKeys.map((key: any) => (
                      <tr key={key.id} className="border-b border-gray-700 hover:bg-gray-700/50">
                        <td className="py-2 px-4 font-mono">{key.name}</td>
                        <td className="py-2 px-4 font-mono text-sm text-gray-400">{key.key_prefix}</td>
                        <td className="py-2 px-4 text-sm">{new Date(key.created_at).toLocaleDateString()}</td>
                        <td className="py-2 px-4 text-sm">{key.last_used}</td>
                        <td className="py-2 px-4">
                          <span className="px-3 py-1 bg-green-600 rounded text-sm capitalize">{key.status}</span>
                        </td>
                        <td className="py-2 px-4">
                          <button
                            onClick={() => handleRevokeApiKey(key.id)}
                            className="text-red-400 hover:text-red-300 font-medium"
                          >
                            Revoke
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* API Guide View */}
      {showGuide && <ApiGuide />}
    </div>
  );
}

function SystemTab({ health, loadHealth }: any) {
  const [restartingService, setRestartingService] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [backendAvailable, setBackendAvailable] = useState(false);
  const [serviceStates, setServiceStates] = useState<Record<string, 'running' | 'stopped'>>({
    postgres: 'stopped',
    redis: 'stopped',
    livekit: 'stopped',
    api: 'stopped',
  });

  // Load service statuses from backend
  const loadServiceStatuses = async () => {
    setLoading(true);
    setBackendAvailable(false);
    try {
      // Create an abort controller with 3-second timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      const response = await fetch('/api/admin/services', {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (response.ok) {
        const services = await response.json();
        if (Array.isArray(services)) {
          const states: Record<string, 'running' | 'stopped'> = {};
          services.forEach((service: any) => {
            states[service.id] = service.status === 'running' ? 'running' : 'stopped';
          });
          setServiceStates(states);
          setBackendAvailable(true); // Backend is available if we got data
        }
      } else {
        setBackendAvailable(false);
      }
    } catch (error) {
      console.warn('Could not load service statuses from backend (using mock data):', error);
      // Keep mock data with all services stopped - backend is unavailable
      setBackendAvailable(false);
    } finally {
      setLoading(false);
    }
  };

  // Load service statuses on component mount
  React.useEffect(() => {
    loadServiceStatuses();
  }, []);

  const handleServiceAction = async (service: string, action: 'start' | 'stop' | 'restart') => {
    setRestartingService(`${service}-${action}`);
    try {
      // Create an abort controller with 3-second timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      const response = await fetch(`/api/admin/services/${service}/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to ${action} ${service}`);
      }

      await response.json();
      
      if (action === 'start') {
        setServiceStates(prev => ({ ...prev, [service]: 'running' }));
      } else if (action === 'stop') {
        setServiceStates(prev => ({ ...prev, [service]: 'stopped' }));
      } else {
        // restart keeps it running
        setServiceStates(prev => ({ ...prev, [service]: 'running' }));
      }
      loadHealth();
    } catch (error) {
      console.error(`Failed to ${action} ${service}:`, error);
    } finally {
      setRestartingService(null);
    }
  };

  return (
    <div>
      {loading && (
        <div className="bg-gray-800 rounded-lg p-6 mb-6 text-center">
          <p className="text-gray-400">Loading service status...</p>
        </div>
      )}

      {!loading && !backendAvailable && (
        <div className="mb-8 bg-yellow-900/30 border border-yellow-600 rounded-lg p-4">
          <h3 className="text-yellow-400 font-semibold mb-1">Backend Unavailable</h3>
          <p className="text-sm text-yellow-300">The backend API server is not currently running. Services are shown as stopped. Start the backend server to manage services.</p>
        </div>
      )}

      {/* System Health */}
      {health && (
        <div className="bg-gray-800 rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4">System Health</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-700 rounded p-4">
              <p className="text-gray-400 text-sm">Status</p>
              <p className="text-2xl font-bold text-green-400">{health.status}</p>
            </div>
            <div className="bg-gray-700 rounded p-4">
              <p className="text-gray-400 text-sm">Version</p>
              <p className="text-2xl font-bold">{health.version}</p>
            </div>
            <div className="bg-gray-700 rounded p-4">
              <p className="text-gray-400 text-sm">Last Updated</p>
              <p className="text-sm font-mono">{new Date(health.timestamp).toLocaleString()}</p>
            </div>
          </div>
        </div>
      )}

      {/* Service Status with Controls */}
      <div className="bg-gray-800 rounded-lg p-6 mb-6">
        <h2 className="text-2xl font-bold mb-4">Service Status & Management</h2>
        {!loading && (
          <div className="space-y-4">
          {[
            { 
              name: 'PostgreSQL Database', 
              id: 'postgres',
              description: 'Primary data store for users, organizations, radios, and system configuration. Critical for all operations.'
            },
            { 
              name: 'Redis Cache', 
              id: 'redis',
              description: 'In-memory cache for session management, real-time data, and performance optimization. Enhances system responsiveness.'
            },
            { 
              name: 'LiveKit Server', 
              id: 'livekit',
              description: 'Real-time communication server for video/audio streams and peer connections. Powers PTT radio sessions.'
            },
            { 
              name: 'API Server', 
              id: 'api',
              description: 'REST API backend service handling all client requests, authentication, and business logic.'
            },
          ].map((service) => {
            const isRunning = serviceStates[service.id] === 'running';
            const isBusy = restartingService !== null;
            
            return (
              <div key={service.id} className="p-4 bg-gray-700 rounded">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold text-lg">{service.name}</span>
                  <span className={`text-sm font-semibold px-4 py-2 rounded whitespace-nowrap ${isRunning ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
                    {isRunning ? '● Running' : '● Stopped'}
                  </span>
                </div>
                <p className="text-sm text-gray-300 mb-3">{service.description}</p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleServiceAction(service.id, 'start')}
                    disabled={isBusy || isRunning || !backendAvailable}
                    className={`px-3 py-2 rounded text-sm font-medium transition ${
                      isRunning || isBusy || !backendAvailable
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                  >
                    {restartingService === `${service.id}-start` ? '⟳ Starting...' : 'Start'}
                  </button>
                  <button
                    onClick={() => handleServiceAction(service.id, 'stop')}
                    disabled={isBusy || !isRunning || !backendAvailable}
                    className={`px-3 py-2 rounded text-sm font-medium transition ${
                      !isRunning || isBusy || !backendAvailable
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-red-600 hover:bg-red-700 text-white'
                    }`}
                  >
                    {restartingService === `${service.id}-stop` ? '⟳ Stopping...' : 'Stop'}
                  </button>
                  <button
                    onClick={() => handleServiceAction(service.id, 'restart')}
                    disabled={isBusy || !isRunning || !backendAvailable}
                    className={`px-3 py-2 rounded text-sm font-medium transition ${
                      !isRunning || isBusy || !backendAvailable
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {restartingService === `${service.id}-restart` ? '⟳ Restarting...' : 'Restart'}
                  </button>
                </div>
              </div>
            );
          })}
          </div>
        )}
      </div>

      {/* System Controls */}
      <div className="flex gap-2">
        <button
          onClick={() => {
            loadServiceStatuses();
            loadHealth();
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Refresh Status
        </button>
        {!backendAvailable && (
          <button
            onClick={loadServiceStatuses}
            className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded"
          >
            Retry Backend Connection
          </button>
        )}
        <button
          className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-2 px-4 rounded"
          title="Restart all services"
        >
          Restart All Services
        </button>
      </div>
    </div>
  );
}

function LogsTab({ systemLogs, loading, level, onRefresh, onClear, onLevelChange }: any) {
  return (
    <div>
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-2xl font-bold">System Logs</h2>
          <div className="flex items-center gap-2">
            <select
              value={level}
              onChange={(e) => onLevelChange(e.target.value)}
              className="bg-gray-700 text-gray-200 text-sm rounded px-3 py-2 border border-gray-600"
            >
              <option value="all">All levels</option>
              <option value="INFO">Info</option>
              <option value="WARNING">Warning</option>
              <option value="ERROR">Error</option>
            </select>
            <button
              onClick={onRefresh}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2 px-4 rounded"
            >
              Refresh
            </button>
            <button
              onClick={onClear}
              className="bg-red-600 hover:bg-red-700 text-white text-sm font-semibold py-2 px-4 rounded"
            >
              Clear
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-gray-400 text-sm py-8 text-center">Loading logs…</div>
        ) : systemLogs.length === 0 ? (
          <div className="text-gray-400 text-sm py-8 text-center">No logs to display.</div>
        ) : (
          <div className="space-y-2 max-h-[32rem] overflow-y-auto">
            {systemLogs.map((log: any) => (
              <div key={log.id} className="bg-gray-700 rounded p-3 font-mono text-sm">
                <div className="flex items-start gap-3">
                  <span className="text-gray-400 whitespace-nowrap">[{log.timestamp}]</span>
                  <span className={`font-bold whitespace-nowrap ${
                    log.level === 'ERROR' ? 'text-red-400' :
                    log.level === 'WARNING' ? 'text-yellow-400' :
                    'text-green-400'
                  }`}>{log.level}</span>
                  {log.source && <span className="text-blue-300 whitespace-nowrap">{log.source}</span>}
                  <span className="text-gray-300 break-all">{log.message}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BackupRestoreTab() {
  const [backups, setBackups] = useState<BackupInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<BackupInfo | null>(null);
  const [confirmText, setConfirmText] = useState('');

  const loadBackups = async () => {
    try {
      setLoading(true);
      const data = await getBackups();
      setBackups(data);
    } catch (err) {
      console.warn('Failed to load backups:', err);
      setBackups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBackups();
  }, []);

  const handleCreate = async (type: 'full' | 'config' | 'postgres') => {
    try {
      setCreating(true);
      setMessage(null);
      const info = await createBackup(type);
      setMessage(`Backup created: ${info.name}`);
      await loadBackups();
    } catch (err) {
      setMessage(`Failed to create backup: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (name: string) => {
    if (!window.confirm(`Delete backup "${name}"? This cannot be undone.`)) return;
    try {
      await deleteBackup(name);
      setMessage(`Deleted: ${name}`);
      await loadBackups();
    } catch (err) {
      setMessage(`Failed to delete backup: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const openRestoreConfirm = (backup: BackupInfo) => {
    setRestoreTarget(backup);
    setConfirmText('');
  };

  const handleRestore = async () => {
    if (!restoreTarget) return;
    try {
      setRestoring(restoreTarget.name);
      setMessage(null);
      await restoreBackup(restoreTarget.name);
      setMessage(`Database restored from: ${restoreTarget.name}`);
      setRestoreTarget(null);
    } catch (err) {
      setMessage(`Failed to restore backup: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setRestoring(null);
    }
  };

  return (
    <div>
      {message && (
        <div className="bg-blue-900/40 border border-blue-600 text-blue-200 rounded-lg p-3 mb-6 text-sm">
          {message}
        </div>
      )}

      {/* Backup Section */}
      <div className="bg-gray-800 rounded-lg p-6 mb-6">
        <h2 className="text-2xl font-bold mb-4">Create Backup</h2>
        <p className="text-gray-400 mb-4">Create a backup of your system configuration and database.</p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleCreate('postgres')}
            disabled={creating}
            className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-bold py-2 px-4 rounded"
            title="A complete pg_dump of the entire Postgres database - every table, restorable exactly as-is"
          >
            {creating ? 'Creating…' : '🐘 Complete Postgres Backup'}
          </button>
          <button
            onClick={() => handleCreate('full')}
            disabled={creating}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold py-2 px-4 rounded"
          >
            {creating ? 'Creating…' : 'Create Full Backup (JSON export)'}
          </button>
          <button
            onClick={() => handleCreate('config')}
            disabled={creating}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold py-2 px-4 rounded"
          >
            Backup Configuration Only
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          <span className="font-semibold">Complete Postgres Backup</span> uses <code>pg_dump</code> and captures every
          table/schema object exactly as stored - this is the only backup type that can be restored. The JSON exports
          are a readable snapshot of the main tables only and cannot be restored from this page.
        </p>
      </div>

      {/* Recent Backups */}
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">Recent Backups</h2>
          <button
            onClick={loadBackups}
            className="bg-gray-700 hover:bg-gray-600 text-gray-200 text-sm font-semibold py-2 px-4 rounded"
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="text-gray-400 text-sm py-8 text-center">Loading backups…</div>
        ) : backups.length === 0 ? (
          <div className="text-gray-400 text-sm py-8 text-center">No backups yet. Create one above.</div>
        ) : (
          <div className="space-y-2">
            {backups.map((b) => (
              <div key={b.name} className="flex items-center justify-between p-3 bg-gray-700 rounded gap-3">
                <div className="min-w-0">
                  <div className="font-mono text-sm text-gray-200 truncate">{b.name}</div>
                  <div className="text-xs text-gray-400">
                    <span
                      className={`inline-block px-2 py-0.5 rounded mr-2 ${
                        b.type === 'config'
                          ? 'bg-purple-900 text-purple-200'
                          : b.type === 'postgres'
                          ? 'bg-emerald-900 text-emerald-200'
                          : 'bg-green-900 text-green-200'
                      }`}
                    >
                      {b.type}
                    </span>
                    {b.size_human} · {b.created_at}
                  </div>
                </div>
                <div className="flex items-center gap-3 whitespace-nowrap">
                  {b.type === 'postgres' && (
                    <button
                      onClick={() => openRestoreConfirm(b)}
                      disabled={restoring === b.name}
                      className="text-amber-400 hover:text-amber-300 text-sm font-semibold disabled:opacity-50"
                    >
                      {restoring === b.name ? 'Restoring…' : 'Restore'}
                    </button>
                  )}
                  <a
                    href={getBackupDownloadUrl(b.name)}
                    className="text-blue-400 hover:text-blue-300 text-sm"
                  >
                    Download
                  </a>
                  <button
                    onClick={() => handleDelete(b.name)}
                    className="text-red-400 hover:text-red-300 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Restore confirmation modal */}
      {restoreTarget && (
        <div data-management-modal className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-red-600 rounded-lg p-6 max-w-lg w-full">
            <h3 className="text-xl font-bold text-red-400 mb-2">⚠️ Restore Database</h3>
            <p className="text-sm text-gray-300 mb-3">
              This will <span className="font-semibold text-red-300">completely replace</span> every table in the
              live database with the contents of:
            </p>
            <p className="font-mono text-sm bg-gray-900 rounded px-3 py-2 mb-3 break-all">{restoreTarget.name}</p>
            <p className="text-sm text-gray-300 mb-4">
              All data created after this backup was taken will be permanently lost. This cannot be undone. Type{' '}
              <span className="font-mono font-bold text-red-300">RESTORE</span> to confirm.
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type RESTORE to confirm"
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setRestoreTarget(null)}
                className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleRestore}
                disabled={confirmText !== 'RESTORE' || restoring !== null}
                className="bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded"
              >
                {restoring ? 'Restoring…' : 'Restore Database'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
